import { Box, CircularProgress, Grid } from "@material-ui/core";
import * as React from "react";
import { Button, List, SelectInput, TextInput, useNotify, useRefresh } from "react-admin";
import Swal from "sweetalert2";
import PbhWaitingResponseFileUploader from "./PbhWaitingResponseFileUploader";
import { PbhRequestedInfoType } from "../../../../Enums/PbhRequestedInfoType";
import { PbhRequestedInfoTypeSelectionChoices } from "../../../../Enums/PbhRequestedInfoType";
import { Separator } from "../../../../Components/Separator";
import { SectionTitle } from "../../../../Components/SectionTitle";
import { Add, Update } from "@material-ui/icons";
import { Form } from "react-final-form";
import {
  PbhWaitingResponseCreateModel,
  PbhWaitingResponseRemarkCreateModel,
} from "../../Models/PbhWaitingResponseCreateModel";
import { WaitingResponseType } from "../../../../Enums/WaitingResponseType";
import { useAppDataProvider } from "../../../../dataprovider";
import { useAuth } from "@airborne/react-admin";
import CancelIcon from "@material-ui/icons/Cancel";
import PbhWaitingResponseFileComponent from "./PbhWaitingResponseFileComponent";
import PbhWaitingResponseCommentComponent from "./PbhWaitingResponseCommentComponent";
import { PbhRequestStatus } from "../../../../Enums/PbhRequestStatus";
import PbhWaitingResponseDataGrid from "./DataGrid/PbhWaitingResponseDataGrid";

const gridContainerProps: any = {
  container: true,
  direction: "row",
  item: true,
  xs: 12,
  spacing: 1,
  justify: "space-around",
  alignItems: "center",
};

const unChangeableStatus = [PbhRequestStatus.Accomplished, PbhRequestStatus.Rejected, PbhRequestStatus.Cancelled];

const PbhWaitingResponse = props => {
  const [loading, setLoading] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(false);
  const [waitingResponse, setWaitingResponse] = React.useState(null);
  const [edittedData, setEdittedData] = React.useState({} as any);
  const [isFileCompShow, setFileCompShow] = React.useState(false);
  const [isCommentCompShow, setCommentCompShow] = React.useState(false);
  const [tempFiles, setTempFiles] = React.useState<any[]>([]);
  const [hasCustomerFileUploaded, setHasCustomerFileUploaded] = React.useState(false);
  
  const dataProvider = useAppDataProvider();
  const { user } = useAuth();
  const notify = useNotify();
  const refresh = useRefresh();
  var createForm: any = null;

  // TT User required file types
  const ttFileRequiredTypes = [PbhRequestedInfoType.CodingSheet, PbhRequestedInfoType.EndUserForm];
  // Customer required file types
  const customerFileRequiredTypes = [
    PbhRequestedInfoType.CodingSheet,
    PbhRequestedInfoType.EndUserForm,
    PbhRequestedInfoType.IPC,
    PbhRequestedInfoType.CMM,
  ];

  const requiresFileUpload = (subjectType: number): boolean => {
    if (user?.isCustomer === false) {
      // TT User requirements
      return ttFileRequiredTypes.includes(subjectType);
    } else {
      // Customer requirements
      return customerFileRequiredTypes.includes(subjectType);
    }
  };

  const createPbhResponseButtonClick = async e => {
    // Check if file upload is required
    if (requiresFileUpload(e.subjectType) && tempFiles.length === 0) {
      Swal.fire({
        title: "Warning!",
        text: "Please upload supportive document",
        icon: "warning",
        iconColor: "#f5a800",
        confirmButtonText: "Ok",
        confirmButtonColor: "#f5a800",
      });
      return;
    }

    setLoading(true);
    let postModel: PbhWaitingResponseCreateModel = new PbhWaitingResponseCreateModel();
    postModel.RequestId = props?.requestId;
    let pbhWaitingResponseRemarkCreateModel = new PbhWaitingResponseRemarkCreateModel();
    pbhWaitingResponseRemarkCreateModel.IsCustomer = user?.isCustomer;
    pbhWaitingResponseRemarkCreateModel.PbhWaitingResponseId = 0;
    pbhWaitingResponseRemarkCreateModel.Remark = e.remark;
    postModel.PbhWaitingResponseRemarkCreateModel = pbhWaitingResponseRemarkCreateModel;
    postModel.PbhRequestedInfoEnum = e.subjectType;
    postModel.WaitingResponseEnum = WaitingResponseType.WaitingResponseFromCustomer;
    dataProvider
      .postPbhWaitingResponse(postModel)
      .then(async result => {
        notify(`Record created`, "success", false, true);
        // Upload files after record is created
        if (tempFiles.length > 0) {
          for (const tempFile of tempFiles) {
            if (tempFile.file) {
              await dataProvider.postPbhWaitingResponseFileRelation(
                { pbhWaitingResponseId: result.data?.id, file: tempFile.file, fileName: tempFile.name },
                { object: { source: "pbhwaitingresponsefilerelations" } }
              );
            }
          }
          setTempFiles([]);
        }
      })
      .catch(result => {
        notify(`${result}`, "error", false, true);
      })
      .finally(() => {
        setLoading(false);
        refresh();
        createForm.reset();
      });
  };

  const editButtonClick = record => {
    setIsEdit(true);
    setEdittedData(record);
    createForm.change("remark", user?.isCustomer ? record?.customerRemark : record?.ttUserRemark);
  };

  const updateButtonClick = values => {
    // Check if file upload is required for Customer
    if (user?.isCustomer && requiresFileUpload(edittedData?.pbhRequestedInfoEnum) && !hasCustomerFileUploaded) {
      Swal.fire({
        title: "Warning!",
        text: "Please upload supportive document",
        icon: "warning",
        iconColor: "#f5a800",
        confirmButtonText: "Ok",
        confirmButtonColor: "#f5a800",
      });
      return;
    }
    proceedWithUpdate(values);
  };

  const proceedWithUpdate = values => {
    setLoading(true);
    let pbhWaitingResponseRemarkCreateModel = new PbhWaitingResponseRemarkCreateModel();
    pbhWaitingResponseRemarkCreateModel.IsCustomer = user?.isCustomer;
    pbhWaitingResponseRemarkCreateModel.PbhWaitingResponseId = edittedData?.id;
    pbhWaitingResponseRemarkCreateModel.Remark = values?.remark;
    dataProvider
      .postPbhWaitingResponseRemark(pbhWaitingResponseRemarkCreateModel)
      .then(result => {
        notify(`Record updated`, "success", false, true);
        setIsEdit(false);
        setEdittedData(null);
        createForm.reset();
      })
      .catch(result => {
        notify(`${result}`, "error", false, true);
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  };

  const cancelButtonClick = record => {
    setLoading(true);
    record.waitingResponseEnum = WaitingResponseType.Cancelled;
    record.cancelledBy = user?.employeeId;
    record.isWaiting = false;
    record.cancelledDate = new Date();
    dataProvider
      .putPbhWaitingResponse(record)
      .then(result => {
        notify(`Record cancelled`, "success", false, true);
      })
      .catch(result => {
        notify(`${result}`, "error", false, true);
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  };

  const sendAgainButtonClick = record => {
    setLoading(true);
    record.waitingResponseEnum = WaitingResponseType.WaitingResponseFromCustomer;
    record.returnedBy = user?.employeeId;
    record.isWaiting = true;
    record.returnedDate = new Date();
    dataProvider
      .putPbhWaitingResponse(record)
      .then(result => {
        notify(`Record cancelled`, "success", false, true);
      })
      .catch(result => {
        notify(`${result}`, "error", false, true);
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  };

  const fileDetailOnClick = record => {
    setWaitingResponse(record);
    setFileCompShow(true);
  };

  const handleFileDetailClose = () => {
    setFileCompShow(false);
    setHasCustomerFileUploaded(true);
    refresh();
  };

  const handleCommentDetailClose = () => setCommentCompShow(false);

  const commentHistoryOnClick = record => {
    setWaitingResponse(record);
    setCommentCompShow(true);
  };

  const editCancelButtonClick = () => {
    setIsEdit(false);
    setEdittedData(null);
    setHasCustomerFileUploaded(false);
    createForm.change("remark", "");
  };

  const isUnChangeable = (): boolean => {
    return props.isPageDisabled || unChangeableStatus.includes(props?.mainData?.requestStatus);
  };

  return (
    <Form
      onSubmit={e => createPbhResponseButtonClick(e)}
      render={({ handleSubmit, form, values }) => {
        createForm = form;
        return (
          <form>
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <>
                    <Separator />
                    <Grid {...gridContainerProps}>
                      <SectionTitle label="Waiting Response From Customer" />
                    </Grid>
                    <Grid container xs={12} sm={12} spacing={8} {...gridContainerProps}>
                      <Grid item xs={4} sm={5}>
                        {isEdit === false && user?.isCustomer === false && (
                          <>
                            <Box flex={1} mr={{ xs: 0, sm: "0.5em" }}>
                              <SelectInput
                                source="subjectType"
                                label="Request Info"
                                choices={PbhRequestedInfoTypeSelectionChoices}
                                optionText="value"
                                optionValue="key"
                                fullWidth
                                variant="outlined"
                                disabled={isUnChangeable()}
                              />
                            </Box>
                            {values?.subjectType && (
                              <Box flex={1} mr={{ xs: 0, sm: "0.5em" }} mt={1}>
                                <PbhWaitingResponseFileUploader
                                  tempFiles={tempFiles}
                                  setTempFiles={setTempFiles}
                                  disabled={isUnChangeable()}
                                />
                              </Box>
                            )}
                          </>
                        )}
                        <Box flex={1} mr={{ xs: 0, sm: "0.5em" }}>
                          {(user?.isCustomer === false || isEdit) && (
                            <TextInput
                              source="remark"
                              label="Remark"
                              multiline
                              fullWidth
                              rows={4}
                              variant="outlined"
                              disabled={isUnChangeable()}
                            />
                          )}
                          </Box>
                        <Box flex={1} mr={{ xs: 0, sm: "0.5em" }}>
                          {isEdit === false && user?.isCustomer === false && (
                            <Button
                              label={"Create"}
                              disabled={loading || isUnChangeable() || !values?.subjectType}
                              size="medium"
                              variant="contained"
                              color="primary"
                              onClick={() => handleSubmit()}
                            >
                              {loading ? <CircularProgress size="2rem" color="secondary" /> : <Add />}
                            </Button>
                          )}
                          {isEdit && (
                            <Button
                              label={"Update"}
                              disabled={loading || isUnChangeable()}
                              size="medium"
                              variant="contained"
                              color="primary"
                              onClick={() => updateButtonClick(values)}
                            >
                              {loading ? <CircularProgress size="2rem" color="secondary" /> : <Update />}
                            </Button>
                          )}
                          {isEdit && (
                            <Button
                              label={"Cancel"}
                              disabled={loading && isUnChangeable()}
                              size="medium"
                              variant="contained"
                              color="primary"
                              style={{ float: "right" }}
                              onClick={editCancelButtonClick}
                            >
                              <CancelIcon />
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} style={{ padding: "30px", overflow: "auto" }}>
                <List
                  {...props}
                  resource="pbhwaitingresponses"
                  basePath="pbhwaitingresponses"
                  perPage={25}
                  bulkActionButtons={false}
                  actions={null}
                  filterDefaultValues={{ requestId__eq: props?.requestId }}
                >
                  <PbhWaitingResponseDataGrid
                    mainData={props?.mainData}
                    cancelButtonClick={cancelButtonClick}
                    sendAgainButtonClick={sendAgainButtonClick}
                    commentHistoryOnClick={commentHistoryOnClick}
                    editButtonClick={editButtonClick}
                    fileDetailOnClick={fileDetailOnClick}
                    setWaitingResponseTabVisible={props.setWaitingResponseTabVisible}
                  />
                </List>
              </Grid>
              <Grid>
                {isFileCompShow && (
                  <PbhWaitingResponseFileComponent
                    {...props}
                    open={isFileCompShow}
                    uploadVisible={props?.mainData?.requestStatus !== PbhRequestStatus.Cancelled}
                    record={waitingResponse}
                    handleClose={handleFileDetailClose}
                  />
                )}
                {isCommentCompShow && (
                  <PbhWaitingResponseCommentComponent
                    {...props}
                    open={isCommentCompShow}
                    uploadVisible={user?.isCustomer}
                    record={waitingResponse}
                    handleClose={handleCommentDetailClose}
                  />
                )}
              </Grid>
            </>
          </form>
        );
      }}
    />
  );
};

export default PbhWaitingResponse;
